export const graphNodeWidth = 360;
export const graphNodeHeight = 68;
export const graphColumnGap = 64;
export const graphRowGap = 96;

export interface LayoutStage {
  id: string;
  label?: string;
  macroPhase?: string;
  height?: number;
}

export interface LayoutMacroPhase {
  id: string;
  label?: string;
}

export interface LayoutTransition {
  id?: string;
  label?: string;
  fromStage: string | null;
  toStage: string;
  featured?: boolean;
}

export interface WorkflowGraphRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WorkflowGraphPort {
  id: string;
  type: 'source' | 'target';
  position: 'top' | 'bottom' | 'left' | 'right';
  x: number;
  y: number;
}

export interface WorkflowGraphLayout {
  width: number;
  height: number;
  nodes: Array<WorkflowGraphRect & { id: string; ports: WorkflowGraphPort[] }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle: string;
    targetHandle: string;
    label: string;
    labelBounds: WorkflowGraphRect;
    points: Array<{ x: number; y: number }>;
  }>;
}

export function getDefaultTransition<T extends LayoutTransition>(
  transitions: T[],
  currentStage?: string,
): T | undefined {
  const outgoingTransitions = transitions.filter(
    transition => transition.fromStage === currentStage,
  );
  return (
    outgoingTransitions.find(transition => transition.featured) ??
    outgoingTransitions[0]
  );
}

export async function buildWorkflowGraphLayout(
  stages: LayoutStage[],
  macroPhases: LayoutMacroPhase[] = [],
  transitions: LayoutTransition[] = [],
): Promise<WorkflowGraphLayout> {
  if (stages.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };
  const stageById = new Map(stages.map(stage => [stage.id, stage]));
  const connections = transitions.flatMap((transition, index) => {
    if (transition.fromStage === null) return [];
    const source = stageById.get(transition.fromStage);
    const target = stageById.get(transition.toStage);
    if (!source || !target) {
      throw new Error('A lifecycle transition references an unknown state.');
    }
    const id =
      transition.id ?? `${transition.fromStage}-${transition.toStage}-${index}`;
    const label =
      transition.label ??
      `${source.label ?? source.id} to ${target.label ?? target.id}`;
    return [
      {
        id,
        source: source.id,
        target: target.id,
        sourceHandle: `${id}::out`,
        targetHandle: `${id}::in`,
        label,
        labelWidth: Math.min(240, Math.max(80, label.length * 7 + 16)),
      },
    ];
  });
  const phaseOrder = new Map(
    macroPhases.map((phase, index) => [phase.id, index]),
  );
  const { default: dagre } = await import('dagre');
  const orderedStages = [...stages].sort(
    (a, b) =>
      (phaseOrder.get(a.macroPhase ?? '') ?? macroPhases.length) -
      (phaseOrder.get(b.macroPhase ?? '') ?? macroPhases.length),
  );
  const directColumns = new Map<string, { x: number; labelWidth: number }>();
  orderedStages.slice(0, -1).forEach((stage, index) => {
    const nextStage = orderedStages[index + 1];
    const adjacent = connections
      .filter(
        candidate =>
          (candidate.source === stage.id &&
            candidate.target === nextStage.id) ||
          (candidate.source === nextStage.id && candidate.target === stage.id),
      )
      .sort(
        (a, b) => Number(a.source === stage.id) - Number(b.source === stage.id),
      );
    const spacing = graphNodeWidth / (adjacent.length + 1);
    adjacent.forEach((edge, column) =>
      directColumns.set(edge.id, {
        x: (column + 1) * spacing,
        labelWidth: Math.min(edge.labelWidth, Math.max(24, 2 * (spacing - 16))),
      }),
    );
  });
  const rowCounts = orderedStages.map(() => 0);
  const routes = connections.map(edge => {
    const sourceIndex = orderedStages.findIndex(
      stage => stage.id === edge.source,
    );
    const targetIndex = orderedStages.findIndex(
      stage => stage.id === edge.target,
    );
    const forward = targetIndex >= sourceIndex;
    const directColumn = directColumns.get(edge.id);
    const direct = directColumn !== undefined;
    const sourceGap = forward ? sourceIndex : sourceIndex - 1;
    const entryGap = forward ? targetIndex - 1 : targetIndex;
    const targetGap = sourceIndex === targetIndex ? sourceGap : entryGap;
    const sourceSlot = rowCounts[sourceGap]++;
    const targetSlot = direct ? sourceSlot : rowCounts[targetGap]++;
    return {
      ...edge,
      sourceIndex,
      targetIndex,
      forward,
      direct,
      directColumn,
      labelWidth: directColumn?.labelWidth ?? edge.labelWidth,
      sourceGap,
      targetGap,
      sourceSlot,
      targetSlot,
    };
  });
  const rowSpacing = 32;
  const gapHeights = rowCounts.map(count =>
    Math.max(graphRowGap, count * rowSpacing + 32),
  );
  const rowOrders = rowCounts.map((count, gap) => {
    const directSlots = routes
      .filter(edge => edge.direct && edge.sourceGap === gap)
      .map(edge => edge.sourceSlot);
    const directSlotSet = new Set(directSlots);
    const outerSlots = Array.from({ length: count }, (_, slot) => slot).filter(
      slot => !directSlotSet.has(slot),
    );
    const split = Math.floor(outerSlots.length / 2);
    return [
      ...outerSlots.slice(0, split),
      ...directSlots,
      ...outerSlots.slice(split),
    ];
  });
  const graph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: 'TB',
    nodesep: graphColumnGap,
    ranksep: graphRowGap,
    marginx: 48,
    marginy: 64,
  });
  orderedStages.forEach(stage =>
    graph.setNode(stage.id, {
      width: graphNodeWidth,
      height: stage.height ?? graphNodeHeight,
    }),
  );
  // A linear backbone keeps branches and return transitions from moving cards sideways.
  orderedStages.slice(0, -1).forEach((stage, index) =>
    graph.setEdge(stage.id, orderedStages[index + 1].id, {
      // Dagre only reserves label height when its width is nonzero.
      width: 1,
      height: gapHeights[index] - graphRowGap,
      labelpos: 'c',
      labeloffset: 0,
    }),
  );
  dagre.layout(graph);
  const { width, height } = graph.graph();
  if (width === undefined || height === undefined) {
    throw new Error('The lifecycle layout engine returned incomplete bounds.');
  }
  const nodes: WorkflowGraphLayout['nodes'] = orderedStages.map(stage => {
    const node = graph.node(stage.id);
    return {
      id: stage.id,
      x: node.x - node.width / 2,
      y: node.y - node.height / 2,
      width: node.width,
      height: node.height,
      ports: [],
    };
  });
  const rowY = (gap: number, slot: number): number => {
    const node = nodes[gap];
    const available = nodes[gap + 1]
      ? nodes[gap + 1].y - node.y - node.height
      : gapHeights[gap];
    return (
      node.y +
      node.height +
      (available - rowCounts[gap] * rowSpacing) / 2 +
      (rowOrders[gap].indexOf(slot) + 0.5) * rowSpacing
    );
  };
  const routedEdges = routes.map(edge => ({
    ...edge,
    sourceY: rowY(edge.sourceGap, edge.sourceSlot),
    targetY: rowY(edge.targetGap, edge.targetSlot),
    lane: 0,
  }));
  const sideWidths = {
    left: Math.max(
      0,
      ...routes
        .filter(edge => !edge.forward && !edge.direct)
        .map(edge => edge.labelWidth),
    ),
    right: Math.max(
      0,
      ...routes
        .filter(edge => edge.forward && !edge.direct)
        .map(edge => edge.labelWidth),
    ),
  };
  for (const forward of [false, true]) {
    const laneEnds: number[] = [];
    routedEdges
      .filter(edge => !edge.direct && edge.forward === forward)
      .sort(
        (a, b) =>
          Math.min(a.sourceY, a.targetY) - Math.min(b.sourceY, b.targetY),
      )
      .forEach(edge => {
        const start = Math.min(edge.sourceY, edge.targetY);
        const end = Math.max(edge.sourceY, edge.targetY);
        const freeLane = laneEnds.findIndex(
          previousEnd => previousEnd + 16 < start,
        );
        edge.lane = freeLane === -1 ? laneEnds.length : freeLane;
        laneEnds[edge.lane] = end;
      });
  }
  const edges = routedEdges.map(edge => {
    const source = nodes[edge.sourceIndex];
    const target = nodes[edge.targetIndex];
    const lateralPortX = edge.forward ? graphNodeWidth - 8 : 8;
    const portX = edge.directColumn?.x ?? lateralPortX;
    const sourcePoint = {
      x: source.x + portX,
      y: source.y + (edge.forward ? source.height : 0),
    };
    const targetAtBottom = !edge.forward || edge.source === edge.target;
    const targetPoint = {
      x: target.x + portX,
      y: target.y + (targetAtBottom ? target.height : 0),
    };
    source.ports.push({
      id: edge.sourceHandle,
      type: 'source',
      position: edge.forward ? 'bottom' : 'top',
      x: portX,
      y: sourcePoint.y - source.y,
    });
    target.ports.push({
      id: edge.targetHandle,
      type: 'target',
      position: targetAtBottom ? 'bottom' : 'top',
      x: portX,
      y: targetPoint.y - target.y,
    });
    const side = edge.forward ? 'right' : 'left';
    const laneOffset = sideWidths[side] + 48 + edge.lane * 32;
    const laneX = edge.forward
      ? source.x + graphNodeWidth + laneOffset
      : source.x - laneOffset;
    const points = edge.direct
      ? [sourcePoint, targetPoint]
      : [
          sourcePoint,
          { x: sourcePoint.x, y: edge.sourceY },
          { x: laneX, y: edge.sourceY },
          { x: laneX, y: edge.targetY },
          { x: targetPoint.x, y: edge.targetY },
          targetPoint,
        ];
    const lateralLabelX = edge.forward
      ? source.x + graphNodeWidth + 16
      : source.x - edge.labelWidth - 16;
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      label: edge.label,
      points,
      labelBounds: {
        x: edge.direct ? source.x + portX - edge.labelWidth / 2 : lateralLabelX,
        y: edge.sourceY - 10,
        width: edge.labelWidth,
        height: 20,
      },
    };
  });
  const bounds = [
    ...nodes,
    ...edges.flatMap(edge => [
      edge.labelBounds,
      ...edge.points.map(point => ({ ...point, width: 0, height: 0 })),
    ]),
  ];
  return {
    width:
      Math.max(...bounds.map(rect => rect.x + rect.width)) -
      Math.min(...bounds.map(rect => rect.x)),
    height:
      Math.max(...bounds.map(rect => rect.y + rect.height)) -
      Math.min(...bounds.map(rect => rect.y)),
    nodes,
    edges,
  };
}
